import React, { useState, useEffect } from 'react';
import {
	Drawer,
	Typography,
	IconButton,
	CircularProgress,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Box,
	Chip
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Timeline,
	TimelineItem,
	TimelineSeparator,
	TimelineConnector,
	TimelineContent,
	TimelineDot,
	TimelineOppositeContent
} from '@mui/lab';
import { getLeadCaptureAnalytics } from '../../../api/serverRequests';
import { useTheme } from '@mui/material/styles';

const useStyles = makeStyles((theme) => ({
	container: {
		width: '100%',
		padding: theme.spacing(4)
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: theme.spacing(2),
		marginBottom: theme.spacing(3),
		borderRadius: theme.shape.borderRadius,
		color: theme.palette.common.white
	},
	content: {
		maxHeight: 'calc(100vh - 160px)',
		overflowY: 'scroll',
		paddingRight: theme.spacing(1)
	},
	accordionRoot: {
		width: '100%',
		marginBottom: theme.spacing(1),
		borderRadius: theme.shape.borderRadius,
		boxShadow: theme.shadows[1]
	},
	timelineContent: {
		paddingTop: theme.spacing(1)
	},
	sectionsWrapper: {
		display: 'flex',
		flexWrap: 'wrap',
		padding: theme.spacing(2)
	},
	chip: {
		marginRight: theme.spacing(1),
		marginBottom: theme.spacing(1)
	},
	sectionLabel: {
		marginRight: theme.spacing(1),
		alignSelf: 'center'
	}
}));

export default function LeadCaptureAnalyticsDrawer({ open, onClose, homeId, listing }) {
	const classes = useStyles();
	const theme = useTheme();
	const [analyticsLoading, setAnalyticsLoading] = useState(false);
	const [analyticsError, setAnalyticsError] = useState(false);
	const [analyticsData, setAnalyticsData] = useState([]);
	const sectionColorMap = {
		market: 'primary',
		active: 'secondary',
		sold: 'default',
		tips: 'primary',
		details: 'default'
	};
	const eventColorMap = {
		click: theme.palette.success ? theme.palette.success.main : theme.palette.primary.main,
		view: theme.palette.info ? theme.palette.info.main : theme.palette.secondary.main,
		scroll: theme.palette.warning ? theme.palette.warning.main : theme.palette.secondary.main
	};

	useEffect(() => {
		if (!open) return;
		if (!homeId) return;
		setAnalyticsLoading(true);
		getLeadCaptureAnalytics(homeId)
			.then((res) => {
				if (res.status === 200) {
					let data = res.data;
					if (data.sessions) data = data.sessions;
					setAnalyticsData(Array.isArray(data) ? data : []);
				}
				setAnalyticsLoading(false);
			})
			.catch(() => {
				setAnalyticsError(true);
				setAnalyticsLoading(false);
			});
	}, [open, homeId]);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{ style: { width: 'min(80vw, 1000px)', zIndex: theme.zIndex.modal + 1 } }}
			ModalProps={{ style: { zIndex: theme.zIndex.modal + 1 } }}
			style={{ zIndex: theme.zIndex.modal + 1 }}
		>
			<div className={classes.container}>
				<div className={classes.header}>
					<Typography variant="h6">User Interactions</Typography>
					<IconButton onClick={onClose} size="large">
						<CloseIcon />
					</IconButton>
				</div>
				<div className={classes.content}>
					{analyticsLoading ? (
						<CircularProgress size={24} />
					) : analyticsError ? (
						<Typography variant="body2">Failed to load analytics</Typography>
					) : analyticsData.length > 0 ? (
						analyticsData.map((session) => {
							const viewedSections = Array.isArray(session.ViewedSections)
								? session.ViewedSections
								: (() => {
										try {
											return JSON.parse(session.ViewedSections);
										} catch {
											return [];
										}
								  })();
							const events = Array.isArray(session.Events)
								? session.Events
								: (() => {
										try {
											return JSON.parse(session.Events);
										} catch {
											return [];
										}
								  })();
							return (
								<Accordion
									key={session.SessionId}
									className={classes.accordionRoot}
								>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Typography variant="body2">
											{new Date(session.SessionStart).toLocaleString()}
										</Typography>
									</AccordionSummary>
									<AccordionDetails style={{ padding: 0 }}>
										<div style={{ width: '100%' }}>
											{viewedSections.length > 0 && (
												<Box className={classes.sectionsWrapper}>
													<Typography
														variant="caption"
														className={classes.sectionLabel}
													>
														Viewed Sections:
													</Typography>
													{viewedSections.map((section, i) => {
														const sectionStr =
															typeof section === 'string'
																? section
																: JSON.stringify(section);
														return (
															<Chip
																key={i}
																label={sectionStr}
																size="small"
																className={classes.chip}
																color={
																	sectionColorMap[
																		sectionStr.toLowerCase()
																	] || 'default'
																}
																variant="outlined"
															/>
														);
													})}
												</Box>
											)}
											{events.length > 0 && (
												<Timeline align="left" style={{ padding: 0 }}>
													{events.map((evt, idx) => (
														<TimelineItem key={idx}>
															<TimelineOppositeContent
																style={{ flex: 0.2 }}
															>
																<Typography variant="caption">
																	{new Date(
																		evt.ts
																	).toLocaleTimeString()}
																</Typography>
															</TimelineOppositeContent>
															<TimelineSeparator>
																<TimelineDot
																	style={{
																		backgroundColor:
																			eventColorMap[
																				evt.type
																			] ||
																			theme.palette.grey[400]
																	}}
																/>
																{idx !== events.length - 1 && (
																	<TimelineConnector />
																)}
															</TimelineSeparator>
															<TimelineContent
																className={classes.timelineContent}
															>
																<Typography variant="body2">
																	{typeof evt.type === 'string'
																		? evt.type
																		: JSON.stringify(evt.type)}
																</Typography>
																{evt.details &&
																	evt.details.section && (
																		<Typography variant="caption">
																			Section:{' '}
																			{typeof evt.details
																				.section ===
																			'string'
																				? evt.details
																						.section
																				: JSON.stringify(
																						evt.details
																							.section
																				  )}
																		</Typography>
																	)}
																{evt.details &&
																	evt.details.name && (
																		<Typography variant="caption">
																			Target:{' '}
																			{typeof evt.details
																				.name === 'string'
																				? evt.details.name
																				: JSON.stringify(
																						evt.details
																							.name
																				  )}
																		</Typography>
																	)}
															</TimelineContent>
														</TimelineItem>
													))}
												</Timeline>
											)}
										</div>
									</AccordionDetails>
								</Accordion>
							);
						})
					) : (
						<Typography variant="body2">No analytics data</Typography>
					)}
				</div>
			</div>
		</Drawer>
	);
}
